import React, { useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../theme';
import {
  useEnableAdcOnlyMutation,
  useEnableAdcWithDeliveryMutation
} from '../../../features/organizations/organizationsApiSlice';

const CookieSyncOnboarding = ({ organization, onClose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const onpremConfig = organization?.s3_config?.infra_data?.onprem_config;
  const isBucketCreated = onpremConfig?.bucket_created || false;
  const bucketName = onpremConfig?.onprem_bucket || '';
  const hasValidS3Location = Boolean(bucketName && isBucketCreated);

  const [enableAdcWithDelivery] = useEnableAdcWithDeliveryMutation();
  const [enableAdcOnly] = useEnableAdcOnlyMutation();

  const [stepStatus, setStepStatus] = useState({
    location: null,
    integration: null,
    complete: null
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEnableAdcWithDelivery = async () => {
    setError('');
    setLoading(true);
    setStepStatus({ location: 'pending', integration: 'pending', complete: 'pending' });

    try {
      // Step 1: Location validation
      if (!bucketName || !isBucketCreated) {
        setStepStatus((prev) => ({ ...prev, location: 'error' }));
        setError('No valid S3 location is configured for this organization.');
        setLoading(false);
        return;
      } else {
        setStepStatus((prev) => ({ ...prev, location: 'success' }));
      }

      // Step 2: API call to handle integration creation + ADC enable
      const result = await enableAdcWithDelivery({ id: organization.organization_id }).unwrap();

      // If integration created or already existed
      setStepStatus((prev) => ({ ...prev, integration: 'success', complete: 'success' }));
      setTimeout(() => {
        setLoading(false);
        alert('ADC with Delivery enabled successfully.');
        onClose();
      }, 500);
    } catch (err) {
      console.error(err);
      setStepStatus((prev) => ({ ...prev, integration: 'error', complete: 'error' }));
      setError(err?.data?.message || 'An unexpected error occurred.');
      setLoading(false);
    }
  };

  const handleEnableAdcOnly = async () => {
    try {
      await enableAdcOnly({ id: organization.organization_id }).unwrap();
      alert('ADC enabled successfully.');
    } catch (error) {
      console.error('Failed to enable ADC:', error);
    }
    onClose();
  };

  const renderStep = (label, status) => {
    const color = {
      success: colors.greenAccent[400],
      pending: colors.grey[300],
      error: colors.redAccent[300]
    }[status];

    const icon = {
      success: '✅',
      pending: <CircularProgress size={16} />,
      error: '❌'
    }[status];

    return (
      <Box display="flex" alignItems="center" mb={1} gap={1}>
        <Box>{icon}</Box>
        <Typography variant="body1" color={color}>
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        background: `linear-gradient(135deg, ${colors.greenAccent[800]}, ${colors.primary[900]})`,
        borderRadius: '20px',
        padding: '20px'
      }}
    >
      <Typography variant="h4" mb={2} color="textPrimary">
        Cookie Sync Configuration
      </Typography>
      <Typography variant="body1" mb={2} color="textPrimary">
        Cookie Sync is our tag that goes on member pages to resolve emails to page visits.
      </Typography>

      <Typography variant="body2" mt={2} color="textPrimary">
        Output location:
        {bucketName && isBucketCreated
          ? ` s3://${bucketName}/outgoing/cookie_sync/`
          : ` No S3 location configured for this organization.`}
      </Typography>

      {loading && (
        <Box mt={3}>
          <Typography variant="h6" mb={1}>
            Enabling ADC + Delivery...
          </Typography>
          {renderStep('✔️ S3 Location configured', stepStatus.location)}
          {renderStep('🔧 Storage Integration ready', stepStatus.integration)}
          {renderStep('🚀 ADC with Delivery configured', stepStatus.complete)}
        </Box>
      )}

      {error && (
        <Typography mt={2} color="error">
          {error}
        </Typography>
      )}

      <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleEnableAdcWithDelivery}
          disabled={loading || !hasValidS3Location}
        >
          Enable ADC + Delivery at the Above Location
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleEnableAdcOnly}
          disabled={loading}
        >
          Enable ADC
        </Button>
      </Box>
    </Paper>
  );
};

export default CookieSyncOnboarding;
